/**
 * Joins first and last name into a single full name string.
 */
export function joinFullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

/**
 * Splits a full name string into first_name and last_name.
 * The first word becomes first_name; everything after is last_name.
 */
export function splitFullName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/);
  const first_name = parts[0] ?? '';
  const last_name = parts.slice(1).join(' ');
  return { first_name, last_name };
}

/**
 * Validates that a full name is a name: at least two words, each end of it at least two letters.
 * Returns an error message string, or null if valid.
 *
 * The LAST word, not the second one. A check that stopped at `parts[1]` let junk hide at the end
 * of a compound name — "Ana Silva X" passed because "Silva" was fine — and a plain "two words"
 * count let "a a" through, which is how a single letter ended up printed on a gate screen as
 * somebody's name.
 *
 * Two letters is the floor because real Brazilian names reach it and stop: "Sá", "Ló", and the
 * particles in "Ana de Sá". Anything stricter starts refusing people.
 *
 * The character check is the backstop for text that never passed through a field filtered by
 * `sanitizePersonName` — a restored draft, a programmatic set, a record that predates the rule.
 * "Morador 123" has the right shape (two words, both ends long enough), so only the characters
 * give it away. The apps apply the identical rule: Android's util/PersonName.kt and iOS's
 * Models/PersonName.swift.
 */
export function validateFullName(full: string): string | null {
  const trimmed = full.trim();
  if (!trimmed) return 'Nome obrigatório';
  if (trimmed !== sanitizePersonName(trimmed).trim()) return 'Nome inválido';
  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) return 'Informe o nome e o sobrenome';
  if (parts[0].length < 2) return 'Primeiro nome muito curto';
  if (parts[parts.length - 1].length < 2) return 'Sobrenome muito curto';
  return null;
}

/**
 * What may be typed into a person's name.
 *
 * A name here is not a label: it reaches the facial readers, the access logs, the PDF reports and
 * the screen a porteiro reads at 2am to decide whether to open a door. The fields used to accept
 * anything, so "Morador 123" and "Teste 1" went straight into production records.
 *
 * LETTERS — Latin, accents and cedilla included (José, Conceição, Núñez, Đỗ) — plus the three
 * marks real full names carry:
 *
 *  - a SPACE, since this is a full name;
 *  - an APOSTROPHE, straight or typographic (D'Ávila, D’Angelo — an iPhone keyboard produces the
 *    second one and a reader cannot tell them apart);
 *  - a HYPHEN (Ana-Maria, Vargas-Llosa).
 *
 * Everything else — digits, @, #, _, brackets, emoji — is dropped AS IT IS TYPED rather than
 * refused on submit. The field simply never shows the character, so nobody has to be taught the
 * rule or chase a validation message to the end of a form.
 *
 * Spelled as explicit ranges rather than `\p{L}`: this project's tsconfig targets ES5, where the
 * `u` flag and Unicode property escapes do not compile. The ranges are
 *   A-Z a-z · À-ÖØ-öø-ÿ and Latin Extended-A/B (\u00C0-\u024F, minus the × and ÷ that sit inside
 *   that block) · combining diacritics (\u0300-\u036F, because many keyboards and most pasted
 *   text deliver "ã" as "a" plus a combining tilde) · Latin Extended Additional (\u1E00-\u1EFF,
 *   Vietnamese and friends).
 *
 * KNOWN LIMIT: a name in a non-Latin script (Cyrillic, Greek, Arabic, CJK) is stripped rather
 * than kept. That is wrong in the abstract and fine for a Brazilian condomínio product; widen the
 * ranges before this ships anywhere it would silently erase somebody's name.
 */
const DISALLOWED_IN_NAME =
  /[^A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u0300-\u036F\u1E00-\u1EFF\s'’-]/g;

/**
 * Strips everything that is not part of a name, as the person types.
 *
 * Inner and trailing spaces are left alone: collapsing or trimming mid-keystroke fights the
 * person at the keyboard, because "Ana " is what a half-typed "Ana Paula" looks like. A LEADING
 * space is removed, so a name never starts blank (and never sorts to the top of a list for it).
 *
 * Trim on submit, not here.
 */
export function sanitizePersonName(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(DISALLOWED_IN_NAME, '').replace(/^\s+/, '');
}

/**
 * Whether a finished value is usable as a name — for submit-time checks, not for typing.
 *
 * Deliberately lenient about length: a one-letter name is somebody's problem to explain, not
 * this function's to refuse. It only answers "is there a letter in here at all".
 */
export function isUsablePersonName(raw: string | null | undefined): boolean {
  return /[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u1E00-\u1EFF]/.test(
    sanitizePersonName(raw).trim()
  );
}
